<!-- Header -->
<div class="flex items-center justify-between gap-4 px-6 py-4 bg-neutral-900 text-white">
  <div class="flex items-center gap-4 min-w-0">
    @if (data.image) {
      <img [src]="data.image" [alt]="data.productName" class="h-14 w-14 rounded-xl object-cover border border-white/10 flex-shrink-0" />
    } @else {
      <span class="h-14 w-14 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0"><mat-icon class="!text-white/50">inventory_2</mat-icon></span>
    }
    <div class="min-w-0">
      <div class="flex flex-wrap items-center gap-2">
        <h1 class="font-bold text-xl leading-tight truncate max-w-[60vw]">{{ data.productName }}</h1>
        @if (data.active === false) {
          <span class="text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-white/15 text-white/80">Inactive</span>
        } @else if (data.active) {
          <span class="text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-emerald-400/20 text-emerald-300">Active</span>
        }
        @if (data.isFeatured) {
          <span class="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-amber-400/20 text-amber-300"><mat-icon class="!text-[13px] !h-[13px] !w-[13px]">star</mat-icon> Featured</span>
        }
      </div>
      @if (product) {
        <div class="mt-1 text-sm text-white/60">
          @if (categoryPath.parent) { {{ categoryPath.parent }} <span class="mx-1">›</span> }
          {{ categoryPath.name }}
        </div>
      }
    </div>
  </div>
  <div class="flex items-center gap-2 flex-shrink-0">
    <a class="hidden sm:flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-sm font-semibold transition-colors"
      [href]="frontEndURL + data.productSlug" target="_blank" rel="noopener">
      <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">open_in_new</mat-icon> View on site
    </a>
    <button (click)="dialogRef.close({ status: false })" class="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors" matTooltip="Close" aria-label="Close">
      <mat-icon class="!text-[20px] !h-5 !w-5">close</mat-icon>
    </button>
  </div>
</div>

@if (progress) {
  <mat-progress-bar mode="indeterminate"></mat-progress-bar>
}

<mat-dialog-content class="mat-typography !p-0 bg-gray-50">
  @if (product) {
    <div class="p-6 flex flex-col gap-6">

      <!-- Key numbers -->
      <div class="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div class="rounded-xl bg-neutral-900 text-white px-4 py-3">
          <div class="text-[10px] font-semibold text-amber-400 uppercase tracking-widest">From</div>
          <div class="text-2xl font-extrabold">£{{ fromPrice | number:'1.2-2' }}</div>
        </div>
        <div class="rounded-xl bg-white border border-gray-100 px-4 py-3">
          <div class="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Options</div>
          <div class="text-2xl font-extrabold text-gray-900">{{ variants.length }}</div>
        </div>
        <div class="rounded-xl bg-white border border-gray-100 px-4 py-3">
          <div class="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Total stock</div>
          <div class="flex items-center gap-2">
            <span class="text-2xl font-extrabold text-gray-900">{{ totalStock | number }}</span>
            <span class="text-[11px] font-semibold px-2 py-0.5 rounded-full" [ngClass]="stockState(totalStock).cls">{{ stockState(totalStock).label }}</span>
          </div>
        </div>
        <div class="rounded-xl border px-4 py-3" [ngClass]="bestSaving > 0 ? 'bg-rose-50 border-rose-100' : 'bg-white border-gray-100'">
          <div class="text-[10px] font-semibold uppercase tracking-widest" [ngClass]="bestSaving > 0 ? 'text-rose-400' : 'text-gray-400'">Offer</div>
          <div class="text-2xl font-extrabold" [ngClass]="bestSaving > 0 ? 'text-rose-600' : 'text-gray-300'">{{ bestSaving > 0 ? '-' + bestSaving + '%' : 'None' }}</div>
        </div>
      </div>

      <!-- Details -->
      <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div class="flex items-center gap-2 mb-3">
          <mat-icon class="!text-amber-500 !text-[20px] !h-5 !w-5">info</mat-icon>
          <h2 class="font-semibold text-gray-900">Details</h2>
        </div>
        <dl class="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <div>
            <dt class="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Product slug</dt>
            <dd class="mt-0.5 flex items-center gap-1.5 font-mono text-gray-800 break-all">
              {{ product.product.productSlug }}
              <button class="h-6 w-6 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 flex items-center justify-center flex-shrink-0" (click)="copyText(product.product.productSlug, 'Slug')" matTooltip="Copy slug">
                <mat-icon class="!text-[14px] !h-[14px] !w-[14px]">content_copy</mat-icon>
              </button>
            </dd>
          </div>
          <div>
            <dt class="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Min price</dt>
            <dd class="mt-0.5 font-semibold text-gray-800">£{{ product.product.minPrice | number:'1.2-2' }}</dd>
          </div>
          <div class="md:col-span-2">
            <dt class="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Google product category</dt>
            <dd class="mt-0.5 text-gray-800">{{ product.product.googleProductCategory || '-' }}</dd>
          </div>
          @if (product.product.productLabels?.length) {
            <div class="md:col-span-2">
              <dt class="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Labels</dt>
              <dd class="mt-1 flex flex-wrap gap-1.5">
                @for (pl of product.product.productLabels; track $index) {
                  <span class="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-100">{{ pl.labels?.title }}</span>
                }
              </dd>
            </div>
          }
        </dl>
      </div>

      <!-- Variants -->
      <div>
        <div class="flex items-center gap-2 mb-3">
          <mat-icon class="!text-amber-500 !text-[20px] !h-5 !w-5">view_list</mat-icon>
          <h2 class="font-semibold text-gray-900">Options</h2>
          <span class="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{{ variants.length }}</span>
        </div>

        <div class="flex flex-col gap-4">
          @for (variant of variants; track variant.productVariantId) {
            <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 grid grid-cols-1 lg:grid-cols-12 gap-5">

              <!-- Who it is -->
              <div class="lg:col-span-5 min-w-0">
                <div class="flex items-center gap-1.5">
                  <span class="font-mono font-bold text-gray-900 break-all">{{ variant.sku }}</span>
                  <button class="h-6 w-6 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 flex items-center justify-center flex-shrink-0" (click)="copyText(variant.sku, 'SKU')" matTooltip="Copy SKU">
                    <mat-icon class="!text-[14px] !h-[14px] !w-[14px]">content_copy</mat-icon>
                  </button>
                </div>
                @if (variant.deliveryterm?.deliveryTermDesc) {
                  <span class="inline-flex items-center gap-1 mt-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700">
                    <mat-icon class="!text-[13px] !h-[13px] !w-[13px]">schedule</mat-icon> {{ variant.deliveryterm.deliveryTermDesc }}
                  </span>
                }
                <div class="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                  @if (variant.weight > 0) {
                    <div class="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2"><div class="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Weight</div><div class="text-sm font-bold text-gray-900">{{ variant.weight | number:'1.0-2' }} kg</div></div>
                  }
                  @if (variant.length > 0 || variant.width > 0 || variant.height > 0) {
                    <div class="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2"><div class="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">L × W × H</div><div class="text-sm font-bold text-gray-900">{{ variant.length || '-' }} × {{ variant.width || '-' }} × {{ variant.height || '-' }}</div></div>
                  }
                  @if (variant.approxTiles > 0) {
                    <div class="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2"><div class="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Tiles</div><div class="text-sm font-bold text-gray-900">{{ variant.approxTiles }}</div></div>
                  }
                  @if (variant.approxCoverage > 0) {
                    <div class="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2"><div class="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Covers</div><div class="text-sm font-bold text-gray-900">{{ variant.approxCoverage | number:'1.0-2' }} m²</div></div>
                  }
                </div>
                @if (variant.barcode) {
                  <div class="mt-3 flex items-center gap-1.5 text-xs text-gray-500">
                    <mat-icon class="!text-[15px] !h-[15px] !w-[15px] !text-gray-400">qr_code_2</mat-icon>
                    <span class="font-mono">{{ variant.barcode }}</span>
                    <button class="h-6 w-6 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 flex items-center justify-center" (click)="copyText(variant.barcode, 'Barcode')" matTooltip="Copy barcode">
                      <mat-icon class="!text-[13px] !h-[13px] !w-[13px]">content_copy</mat-icon>
                    </button>
                  </div>
                }
              </div>

              <!-- Price -->
              <div class="lg:col-span-3 lg:border-l lg:border-gray-100 lg:pl-5">
                <div class="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Price</div>
                @if (savingPercent(variant) > 0) {
                  <div class="text-sm text-gray-400 line-through">£{{ variant.price | number:'1.2-2' }}</div>
                  <div class="flex items-center gap-2">
                    <span class="text-2xl font-extrabold text-emerald-700">£{{ effectivePrice(variant) | number:'1.2-2' }}</span>
                    <span class="text-[11px] font-bold px-2 py-0.5 rounded-full bg-rose-500 text-white">-{{ savingPercent(variant) }}%</span>
                  </div>
                } @else {
                  <div class="text-2xl font-extrabold text-gray-900">£{{ variant.price | number:'1.2-2' }}</div>
                }
                @if (variant.priceMS > 0) {
                  <div class="mt-2 text-xs text-gray-500">£{{ variant.priceMS | number:'1.2-2' }} per m²</div>
                }
              </div>

              <!-- Stock -->
              <div class="lg:col-span-4 lg:border-l lg:border-gray-100 lg:pl-5">
                <div class="flex items-center justify-between">
                  <div class="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Stock</div>
                  <span class="text-[11px] font-semibold px-2 py-0.5 rounded-full" [ngClass]="stockState(variant.stockQty).cls">{{ stockState(variant.stockQty).label }}</span>
                </div>
                <div class="text-2xl font-extrabold text-gray-900">{{ variant.stockQty | number }}</div>

                <div class="mt-3 flex flex-col gap-2.5">
                  <div>
                    <div class="flex items-center justify-between text-xs">
                      <span class="font-semibold text-emerald-700">Markstey</span>
                      <span class="flex items-center gap-1.5 font-bold text-gray-800">
                        {{ variant.mStockQty | number }}
                        @if (canEditStock && editingStockVariantId !== variant.productVariantId) {
                          <button
                            (click)="openStockEdit(variant.productVariantId)"
                            class="px-2 py-0.5 rounded-md bg-sky-50 text-sky-600 hover:bg-sky-500 hover:text-white text-[11px] font-semibold transition-colors"
                          matTooltip="Adjust Markstey stock">Edit</button>
                        }
                      </span>
                    </div>
                    <div class="mt-1 h-1.5 rounded-full bg-gray-100 overflow-hidden"><div class="h-full rounded-full bg-emerald-500" [style.width.%]="yardShare(variant, 'm')"></div></div>
                  </div>
                  <div>
                    <div class="flex items-center justify-between text-xs">
                      <span class="font-semibold text-amber-700">Kettering</span>
                      <span class="font-bold text-gray-800">{{ variant.kStockQty | number }}</span>
                    </div>
                    <div class="mt-1 h-1.5 rounded-full bg-gray-100 overflow-hidden"><div class="h-full rounded-full bg-amber-400" [style.width.%]="yardShare(variant, 'k')"></div></div>
                  </div>
                </div>

                <!-- Inline stock edit -->
                @if (editingStockVariantId === variant.productVariantId) {
                  <div class="mt-3 p-3 rounded-xl bg-sky-50/60 border border-sky-200 flex flex-col gap-2">
                    <label class="text-xs font-semibold text-gray-600">Adjust Markstey stock (+ to add, - to remove)</label>
                    <input type="number" [(ngModel)]="stockAdjustment"
                      class="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                      placeholder="e.g. 10 or -5" />
                    <div class="flex gap-2">
                      <button (click)="saveStockUpdate(variant)" [disabled]="stockUpdateProgress || stockAdjustment === 0"
                      class="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-xs font-semibold transition-colors">{{ stockUpdateProgress ? 'Saving...' : 'Save' }}</button>
                      <button (click)="cancelStockEdit()" [disabled]="stockUpdateProgress"
                      class="px-4 py-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-40 text-gray-600 text-xs font-semibold transition-colors">Cancel</button>
                    </div>
                  </div>
                }
              </div>
            </div>
          }
        </div>
      </div>

      <!-- Components (composite fencing) -->
      @if (product.product.productcomponents?.length) {
        <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div class="flex items-center gap-2 mb-1">
            <mat-icon class="!text-amber-500 !text-[20px] !h-5 !w-5">construction</mat-icon>
            <h2 class="font-semibold text-gray-900">Components</h2>
          </div>
          <p class="text-xs text-gray-500 mb-3">Total WPC fencing board with PVC in running metres: 1.97</p>
          <div class="flex flex-col divide-y divide-gray-50 rounded-xl border border-gray-100 overflow-hidden">
            @for (productComponent of product.product.productcomponents; track $index) {
              <div class="flex items-center justify-between px-4 py-2.5 text-sm bg-white">
                <span class="text-gray-800">{{ productComponent.componentName }}</span>
                <span class="font-semibold text-gray-900">{{ productComponent.operator == "M" ? 1 * productComponent.operand + productComponent.quantity : 1 + productComponent.operand }} Qty</span>
              </div>
            }
          </div>
        </div>
      }
    </div>
  }
</mat-dialog-content>
